import type {
	ApprovalType,
	ReviewableProfile,
	ReviewableRealtorProfile,
} from "@shared/types";
import { apiClient } from "@/lib/api-client";

export type { ApprovalType, ReviewableProfile, ReviewableRealtorProfile };

/** What each queue answers: the realtors queue carries the realtor fields. */
export interface PendingProfileByType {
	designers: ReviewableProfile;
	contractors: ReviewableProfile;
	realtors: ReviewableRealtorProfile;
}

export async function listPendingProfiles<T extends ApprovalType>(
	type: T,
): Promise<PendingProfileByType[T][]> {
	const response = await apiClient.get(`/api/v1/admin/${type}/pending`);
	return response.data.data[type] as PendingProfileByType[T][];
}

/**
 * The ID proof a realtor submitted, as a Blob typed with the file's content
 * type. Fetched through the API client rather than linked, so a request made
 * after the access token expired is refreshed and retried like every other
 * admin call.
 */
export async function fetchRealtorIdProof(realtorId: string): Promise<Blob> {
	const response = await apiClient.get(
		`/api/v1/admin/realtors/${realtorId}/id-proof`,
		{ responseType: "blob" },
	);
	return response.data as Blob;
}

export async function approveProfile(
	type: ApprovalType,
	id: string,
	notes?: string,
) {
	const trimmedNotes = notes?.trim();
	const response = await apiClient.post(`/api/v1/admin/${type}/${id}/verify`, {
		...(trimmedNotes ? { notes: trimmedNotes } : {}),
	});
	return response.data.data.profile as ReviewableProfile;
}

export async function rejectProfile(
	type: ApprovalType,
	id: string,
	reason: string,
) {
	const response = await apiClient.post(`/api/v1/admin/${type}/${id}/reject`, {
		reason: reason.trim(),
	});
	return response.data.data.profile as ReviewableProfile;
}
